import { Alert, Backdrop, Box, Grid, ImageListItem, ImageListItemBar, Pagination, Stack, TextField } from '@mui/material';
import { AppImageDimension } from 'app/appImages';
import { MprcContainer, MprcSpinnerIndicator } from "components";
import { EditImage, ImageSelectionProps, ImageSelectionResult } from "components/MprcImageHandler";
import { ImageUtils } from 'components/MprcImageHandler/ImageResizer';
import { ExtractErrorInformation } from "components/MprcRequestErrorReport";
import { useMprcTheme } from "components/MprcThemeProvider";
import { IconButtonClearSearch } from 'components/iconButtons/IconButtonClearSearch';
import { GoogleSearchImageItem, GoogleSearchImageResult } from "generated/CoreApi_v1";
import { Formaters } from 'lib/Formaters';
import LibUtils from 'lib/LibUtils';
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import * as React from 'react';
import { useEffect, useRef, useState } from "react";
import "react-image-crop/dist/ReactCrop.css";
import { SessionService } from "services/sessionService";
import { ImageHandlerStyles } from "./ImageHandlerStyles";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const CloudTakeRecords = 24;

const InitialFilterState: ICloudImageState =
{
    lastSearchTerm: "",
    loading: false,
    page: 1,
    data: null,
};

interface ICloudImageState {
    lastSearchTerm: string;
    data: GoogleSearchImageResult | null;
    loading: boolean;
    page: number;
}

interface ICloudImageProps {
    searchTerm?: string;
    size?: "small" | "large";
    selectedImage: GoogleSearchImageItem | null;
    onSelectImage: (image: GoogleSearchImageItem | null) => void;
}

type Props = ICloudImageProps & ImageSelectionProps;

export const CloudImage = (props: Props) => {
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();
    const { classes, cx } = ImageHandlerStyles();

    const { theme } = useMprcTheme();
    const label = theme.MprcImageSelection.searchForLabel;
    const placeholder = theme.MprcImageSelection.searchImagePlaceholder;

    const [searchTerm, setSearchTerm] = useState<string>(props.searchTerm ?? "");
    const [state, setState] = useState<ICloudImageState>(InitialFilterState);

    const { data } = state;
    const { fitMode, dimension, allowEdit, maxSize, backgroundColor, selectedImage, onSelectImage } = props;

    const [imageResult, setImageResult] = useState<ImageSelectionResult | null>(null);

    const searchRef = useRef<HTMLInputElement>(undefined);

    //const themeMui = useTheme();
    //const greaterThanLg = useMediaQuery(themeMui.breakpoints.up("lg"));

    ///
    const getProxyUrl = selectedImage
        ? `${SessionService.current.apiPaths.coreApi}/api/v1.0/WebSearch/ImageProxy?url=${selectedImage.contentUrl}`
        : null;

    ///
    const getProxyUrlThumb = selectedImage
        ? `${SessionService.current.apiPaths.coreApi}/api/v1.0/WebSearch/ImageProxy?url=${selectedImage.thumbnailUrl}`
        : null;

    ///
    const load = (page: number) => {
        if (state.loading || searchTerm.length == 0)
            return;

        setState(state => ({ ...state, loading: true, page }));

        api.v1.webSearch.imageSearch({
            search: searchTerm,
            maxSize: maxSize,
            offset: (page - 1) * CloudTakeRecords,
            take: CloudTakeRecords
        })
            .then((result) => {
                setState(state => ({
                    ...state,
                    loading: false,
                    data: result.data,
                    lastSearchTerm: searchTerm
                }));
            })
            .catch((error) => {
                setState(state => ({ ...state, loading: false }));
                const e = ExtractErrorInformation(error);
                if (e.message) enqueueSnackbar(e.message, { variant: "error" });
            })
            ;
    };

    ///
    const cleanFilter = () => {
        setSearchTerm("");
        setState(InitialFilterState);
        if (searchRef.current) searchRef.current.focus();
    };

    ///
    const selectImage = (item: GoogleSearchImageItem) => {
        onSelectImage(item);
        setImageResult(null);
    };

    ///
    const handleCancelSelection = () => {
        props.onChange(null);
    };

    ///
    const onLoadImageError = () => {
        props.onError(theme.MprcImageSelection.cloudImageLoadError);
        handleCancelSelection();
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    const imageChanged = (dataUrl: string) => {
        const result = {
            source: {
                sourceDataOrUrl: dataUrl,
                name: selectedImage!.filename,
                contentType: selectedImage!.encodingFormat
            },
            editedImage: null
        };
        setImageResult(result);
        props.onChange(result);
    };

    ///
    useEffect(() => {
        if (selectedImage) {
            if (!props.allowEdit) {
                ImageUtils.readImageWithMinimiumSize(getProxyUrl!, selectedImage!.encodingFormat, AppImageDimension.ThumbnailLg, backgroundColor)
                    .then((sourceData) => {
                        imageChanged(sourceData.dataUrl);
                    })
                    .catch((e) => {
                        // Tenta carregar pela URL do Bing e força extensão PNG.
                        ImageUtils.readImageWithMinimiumSize(getProxyUrlThumb!, "image/png", AppImageDimension.ThumbnailLg, backgroundColor)
                            .then((sourceData) => {
                                if (!selectedImage.filename.endsWith(".png")) selectedImage.filename += ".png";
                                imageChanged(sourceData.dataUrl);
                            })
                            .catch((e) => {
                                console.error(e);
                                enqueueSnackbar(theme.MprcImageSelection.fileConversionError, { variant: "error" });
                                onSelectImage(null);
                            });
                    });
            }
            else {
                setImageResult(null);
            }
        }
    }, [selectedImage]);

    useEffect(() => {
        load(1);
    }, [props.searchTerm]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    // const rowMinHeight = 150;
    // const columnsCount = props.size == "large" ? greaterThanLg ? 6 : 4 : 3;
    let pageCount = Math.ceil((data?.total ?? 0) / CloudTakeRecords);
    if (pageCount > 1000) pageCount = 1000;

    return (
        <Grid container direction="column" wrap="nowrap" height="100%" spacing={1}>
            {
                !selectedImage &&
                <Grid>
                    <TextField
                        label={label}
                        placeholder={placeholder}
                        fullWidth={true}
                        margin={"dense"}
                        disabled={state.loading}
                        autoFocus={true}
                        value={searchTerm}
                        slotProps={{
                            input: {
                                endAdornment: searchTerm.length > 0 &&
                                    <IconButtonClearSearch
                                        disableFocusRipple
                                        disableRipple
                                        onClick={cleanFilter}
                                        style={{ padding: 0 }}
                                    />,
                                inputRef: searchRef
                            }
                        }}
                        onBlur={
                            () => {
                                if (searchTerm != state.lastSearchTerm) load(1);
                            }
                        }
                        onKeyUp={
                            (e) => {
                                if (e.keyCode == 13 && searchTerm != state.lastSearchTerm) load(1);
                            }
                        }
                        onChange={
                            (event: React.ChangeEvent<HTMLInputElement>) => {
                                setSearchTerm(event.target.value);
                            }}
                    />
                </Grid>
            }

            <Grid size="grow" height="100%">
                {
                    state.loading
                        ? <MprcSpinnerIndicator />
                        : data && data.count == 0
                            ? <Alert severity="warning">{theme.MprcImageSelection.cloudImageNotFound}</Alert>
                            : data && !selectedImage
                                ? (
                                    <Stack direction="column" alignItems="center" spacing={2} justifyContent="center" height="100%">
                                        <Box height={props.size == "large" ? "60vh" : "308px"} mb={1} overflow="auto" width="100%">
                                            <MprcContainer margin="none" position="relative">
                                                <Backdrop open={state.loading} sx={{ position: "absolute", zIndex: (theme) => theme.zIndex.drawer + 1 }} />
                                                <Box
                                                    display="flex"
                                                    flexWrap="wrap"
                                                    gap={2}
                                                >
                                                    {
                                                        data.items.map((v) => (
                                                            <Box width="175px" height="98px" sx={{ cursor: "pointer" }}>
                                                                <ImageListItem
                                                                    key={v.thumbnailUrl}
                                                                    sx={{ width: "inherit", height: "inherit !important" }}
                                                                    cols={1}
                                                                    title={`${v.description || v.name} - ${v.width}x${v.height} - ${Formaters.fileSize(v.contentSize)}`}
                                                                    onClick={() => selectImage(v)}
                                                                >
                                                                    <img src={v.thumbnailUrl} alt={v.description || v.name} />

                                                                    <ImageListItemBar
                                                                        title={v.name}
                                                                        sx={{ height: "28px", fontSize: "11px" }}
                                                                    />
                                                                </ImageListItem>
                                                            </Box>
                                                        ))
                                                    }
                                                </Box>
                                            </MprcContainer>
                                        </Box>
                                        <Pagination
                                            shape="rounded"
                                            color="primary"
                                            page={state.page}
                                            count={pageCount}
                                            onChange={(e, pageNumber) => load(pageNumber)}
                                        />
                                    </Stack>
                                )
                                : selectedImage != null && !allowEdit
                                    ? (
                                        <div className={cx(classes.imageBorder, classes.imageContainer)} >
                                            <>
                                                {
                                                    imageResult != null
                                                        ? (
                                                            <img
                                                                src={imageResult.source.sourceDataOrUrl!}
                                                                onError={onLoadImageError}
                                                            />
                                                        )
                                                        : <MprcSpinnerIndicator />
                                                }
                                            </>
                                        </div>
                                    )
                                    : selectedImage != null && allowEdit && dimension
                                        ? (
                                            <EditImage
                                                fitMode={fitMode}
                                                src={getProxyUrl!}
                                                fallbackUrl={selectedImage.thumbnailUrl}
                                                dimension={dimension}
                                                mimetype={selectedImage.encodingFormat}
                                                backgroundColor={backgroundColor}
                                                onError={props.onError}
                                                onChange={
                                                    (dataUrl) => {
                                                        props.onChange({
                                                            source: {
                                                                sourceDataOrUrl: selectedImage!.contentUrl,
                                                                name: selectedImage!.filename,
                                                                contentType: selectedImage!.encodingFormat
                                                            },
                                                            editedImage: {
                                                                sourceDataOrUrl: dataUrl.dataUrl,
                                                                name: LibUtils.getFilenameWithoutExtension(selectedImage!.filename) + ".png",
                                                                contentType: "image/png"
                                                            }
                                                        });
                                                    }}

                                            />
                                        )
                                        : null
                }

            </Grid>
        </Grid>
    );
};
